import { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Search,
  CheckCircle,
  BarChart,
  Award,
  Users,
  DollarSign,
  Github,
  Linkedin,
  Mail,
} from "lucide-react";

// Component Imports
import FloatingCursor from "./components/FloatingCursor";
import FAQItem from "./components/FAQItem";
import ContactForm from "./components/ContactForm";
import InfoCard from "./components/InfoCard";
import ProjectRow from "./components/ProjectRow";

// --- FULLY TRANSLATED & EXPANDED CONTENT (EN, KZ, RU) ---
const translations = {
  en: {
    hero_title: "No Website? You're Leaving Money on the Table.",
    hero_subtitle:
      "In today's market, your website is your most powerful marketing tool. We build affordable, high-performance websites that attract new clients and build trust instantly.",
    hero_cta: "Get My Free Consultation",
    why_title: "A Great Website Isn't an Expense—It's an Investment.",
    why_card_1_title: "Get Discovered 24/7",
    why_card_1_desc:
      "Appear on Google and attract local customers who are actively searching for the services you offer.",
    why_card_2_title: "Build Instant Credibility",
    why_card_2_desc:
      "A professional site shows you're a serious business, building more trust than a social media profile ever could.",
    why_card_3_title: "Automate Your Growth",
    why_card_3_desc:
      "Your website works for you around the clock, capturing leads, booking appointments, and generating sales.",
    projects_title: "Businesses We've Helped Thrive",
    testimonials_title: "What Our Clients Say",
    why_us_title: "Why Choose Brazilia Works?",
    why_us_card_1_title: "The Perfect Balance",
    why_us_card_1_desc:
      "Get a fully custom, high-quality website without the huge agency price tag. We're the ideal choice for small businesses.",
    why_us_card_2_title: "We Are Your Partners",
    why_us_card_2_desc:
      "We are a small, dedicated team. We take the time to understand your goals and are personally invested in your success.",
    why_us_card_3_title: "Transparent Pricing",
    why_us_card_3_desc:
      "No surprises or hidden fees. We provide a clear, fixed quote for your entire project, starting from just $200.",
    faq_title: "Frequently Asked Questions",
    contact_title: "Have an Idea? Let's Talk.",
    // --- REVISED TEXT: More confident and concise ---
    contact_subtitle:
      "Have a project in mind or just a question? I'd be happy to help.",
    form_name_label: "Full Name",
    form_email_label: "Email Address",
    form_message_label: "Tell us about your project",
    form_submit_btn: "Send Message",
    form_alt_title: "Prefer a Live Chat?",
    form_alt_desc:
      "Book a free, 15-minute discovery call to discuss your ideas directly with our team. No pressure, no obligations.",
    form_alt_cta: "Book a Discovery Call",
    form_email_direct_label: "Or email us directly:",
    footer_tagline:
      "Building digital storefronts that deliver. © 2024 Brazilia Works.",
    projects: [
      {
        title: "Urazbakhova & Partners",
        type: "Corporate Law Firm Website",
        description:
          "An elegant and professional website for Urazbakhova & Partners, designed to project authority and trust. The site showcases the firm's legal team, details their corporate law practice areas, and builds credibility through powerful client testimonials and official letters of recommendation.",
        features: [
          "Elegant & Authoritative Design",
          "Detailed Practice Areas",
          "Client Testimonials",
          "Multilingual Functionality",
        ],
        image: "/images/uip-1.png",
        domain: "https://uiplaw.kz",
      },
      {
        title: "Molshylyq",
        type: "E-commerce & Social Impact",
        description:
          "Kazakhstan’s first platform for reducing food waste. This multi-role application connects sellers with surplus food to consumers, creating a community with real impact.",
        features: ["Full-Stack App", "PostgreSQL", "Prisma", "Localization"],
        image: "/images/molshylyq-1.png",
        domain: "https://molshylyq.vercel.app/",
      },
      {
        title: "Sayahat Inc",
        type: "Frontend Showcase",
        description:
          "A visually polished landing page for a mock tour agency, built to demonstrate pixel-perfect layouts, responsive design, and smooth animations.",
        features: ["React", "Tailwind CSS", "Framer Motion", "UI/UX Design"],
        image: "/images/sayahat-1.png",
        domain: "https://sayahat-eta.vercel.app",
      },
      {
        title: "Dayne",
        type: "AI Productivity Tool",
        description:
          "A minimalist study tool that uses AI to generate custom flashcards and tests from any text content or file, designed for maximum focus and retention.",
        features: ["AI Prompting", "Supabase", "File Parsing", "Bilingual UI"],
        image: "/images/study-app-1.png",
        domain: "https://study-app-three-black.vercel.app",
      },
      {
        title: "Branddos",
        type: "AI SaaS Platform",
        description:
          "An AI-powered social media assistant that streamlines content strategy for individuals and businesses. We took this from concept to a monetizable live product.",
        features: [
          "AI Integration",
          "Next.js",
          "Vercel",
          "User Authentication",
        ],
        image: "/images/branddos-1.png",
        domain: "https://branddos-aminas-projects-72d2f3c3.vercel.app/",
      },
    ],
    testimonials: [
      {
        name: "Aisha Karimova",
        business: "Orleu Fitness Gym",
        quote:
          "Brazilia Works gave our gym the professional online presence we desperately needed. Bookings have increased by 40% since launch!",
      },
      {
        name: "David Chen",
        business: "Chen's Cleaners, UK",
        quote:
          "The process was seamless and incredibly fast. Our new website looks amazing and perfectly represents our brand. Highly recommended.",
      },
      {
        name: "Elena Ivanova",
        business: "Ademi Beauty Salon",
        quote:
          "I was worried about the cost, but their transparent pricing was a breath of fresh air. They delivered exactly what they promised, on time and on budget.",
      },
    ],
    faqs: [
      {
        q: "How long does it take to build a website?",
        a: "A standard business website typically takes 1-3 weeks from our initial consultation to launch. This can vary depending on the project's complexity.",
      },
      {
        q: "What do you need from me to get started?",
        a: "Just your ideas! We need your vision, any text or images you already have (we can help with this), and your business goals. We'll handle the rest.",
      },
      {
        q: "Can I update the website myself after it's live?",
        a: "Absolutely. We build on user-friendly platforms and provide you with training so you can easily update content.",
      },
    ],
  },
  kz: {
    hero_title:
      "Нарықта көрініп, бәсекелестіктен озыңыз. Сапалы сайт – цифрлық бет-бейнеңіз.",
    hero_subtitle:
      "Бүгінгі нарықта сіздің веб-сайтыңыз – ең қуатты маркетинг құралыңыз. Біз жаңа клиенттерді тартып, сенімділікті лезде арттыратын қолжетімді, жоғары өнімді веб-сайттар жасаймыз.",
    hero_cta: "Тегін кеңес алу",
    why_title: "Жақсы веб-сайт – шығын емес, өсуге салынған инвестиция.",
    why_card_1_title: "24/7 Табылу Мүмкіндігі",
    why_card_1_desc:
      "Google-да пайда болып, сіз ұсынатын қызметтерді белсенді іздеп жүрген жергілікті клиенттерді тартыңыз.",
    why_card_2_title: "Лезде Сенім Орнату",
    why_card_2_desc:
      "Кәсіби сайт сіздің маңызды бизнес екеніңізді көрсетеді, бұл әлеуметтік желі профилінен әлдеқайда көп сенім тудырады.",
    why_card_3_title: "Өсуді Автоматтандыру",
    why_card_3_desc:
      "Веб-сайтыңыз сіз үшін тәулік бойы жұмыс істеп, тапсырыстар жинайды, кездесулер белгілейді және сатылымды арттырады.",
    projects_title: "Біз Өркендетуге Көмектескен Бизнестер",
    testimonials_title: "Клиенттеріміздің Пікірлері",
    why_us_title: "Неге Brazilia Works-ты таңдау керек?",
    why_us_card_1_title: "Мінсіз тепе-теңдік",
    why_us_card_1_desc:
      "Үлкен агенттік бағасынсыз, толықтай тапсырыспен жасалған, жоғары сапалы веб-сайтқа ие болыңыз. Біз шағын бизнес үшін ең жақсы таңдаумыз.",
    why_us_card_2_title: "Біз Сіздің Серіктесіңізбіз",
    why_us_card_2_desc:
      "Біз шағын, ісіне берілген командамыз. Мақсаттарыңызды түсінуге уақыт бөлеміз және сіздің жетістігіңізге жеке мүдделіміз.",
    why_us_card_3_title: "Ашық Баға",
    why_us_card_3_desc:
      "Күтпеген немесе жасырын төлемдер жоқ. Біз сіздің бүкіл жобаңызға 80 000 теңгеден басталатын нақты, бекітілген баға ұсынамыз.",
    faq_title: "Жиі Қойылатын Сұрақтар",
    contact_title: "Идеяңыз бар ма? Талқылайық.",
    // --- REVISED TEXT: More confident and concise ---
    contact_subtitle:
      "Ойыңызда жоба бар ма, әлде жай сұрақ па? Көмектесуге қуаныштымын.",
    form_name_label: "Толық Аты-жөніңіз",
    form_email_label: "Email мекенжайыңыз",
    form_message_label: "Жобаңыз туралы айтып беріңіз",
    form_submit_btn: "Хабарлама Жіберу",
    form_alt_title: "Тікелей сөйлескенді қалайсыз ба?",
    form_alt_desc:
      "Идеяларыңызды командамызбен тікелей талқылау үшін тегін, 15 минуттық таныстыру қоңырауына жазылыңыз. Ешқандай қысым, ешқандай міндеттемелер жоқ.",
    form_alt_cta: "Қоңырауға Жазылу",
    form_email_direct_label: "Немесе тікелей email-ге жазыңыз:",
    footer_tagline:
      "Нәтижеге жеткізетін сандық витриналар жасаймыз. © 2024 Brazilia Works.",
    projects: [
      {
        title: "Urazbakhova & Partners",
        type: "Заң фирмасы",
        description:
          "Urazbakhova & Partners заң фирмасына арналған, бедел мен сенімділікті көрсететін салмақты әрі кәсіби сайт. Сайт фирманың заңгерлер тобын таныстырады, корпоративтік құқық саласындағы қызметтерін сипаттайды және клиенттердің пікірлері мен ресми ұсыныс хаттары арқылы сенімділікті арттырады.",
        features: [
          "Салмақты және беделді дизайн",
          "Қызмет салаларының сипаттамасы",
          "Клиенттердің пікірлері",
          "Үштілді функционалдылық",
        ],
        image: "/images/uip-1.png",
        domain: "https://www.uiplaw.kz",
      },
      {
        title: "Molshylyq",
        type: "E-commerce & Әлеуметтік Жоба",
        description:
          "Қазақстандағы азық-түлік ысырабын азайтуға арналған алғашқы платформа. Бұл көп рөлді қосымша артық азық-түлігі бар сатушыларды тұтынушылармен байланыстырып, нақты әсер ететін қауымдастық құрады.",
        features: [
          "Full-Stack Қосымша",
          "PostgreSQL",
          "Prisma",
          "Жергіліктендіру",
        ],
        image: "/images/molshylyq-1.png",
        domain: "https://molshylyq.vercel.app/",
      },
      {
        title: "Sayahat Inc",
        type: "Frontend Үлгісі",
        description:
          "Жалған туристік агенттікке арналған, пиксельге дейін дәл макеттерді, бейімделгіш дизайнды және бірқалыпты анимацияларды көрсету үшін жасалған, визуалды түрде тартымды лендинг беті.",
        features: ["React", "Tailwind CSS", "Framer Motion", "UI/UX Дизайн"],
        image: "/images/sayahat-1.png",
        domain: "https://sayahat-eta.vercel.app",
      },
      {
        title: "Dayne",
        type: "AI Өнімділік Құралы",
        description:
          "Кез-келген мәтіннен немесе файлдан жасанды интеллект арқылы арнайы флеш-карталар мен тесттер жасайтын минималистік оқу құралы. Ол максималды зейін мен есте сақтауға арналған.",
        features: ["AI Промптинг", "Supabase", "Файлды талдау", "ҮШтілді UI"],
        image: "/images/study-app-1.png",
        domain: "https://study-app-three-black.vercel.app",
      },
      {
        title: "Branddos",
        type: "AI SaaS Платформасы",
        description:
          "Жеке тұлғалар мен бизнес үшін контент стратегиясын оңтайландыратын жасанды интеллект негізіндегі әлеуметтік медиа көмекшісі. Біз бұл жобаны идеядан бастап монетизацияланатын өнімге дейін жеткіздік.",
        features: [
          "AI Интеграциясы",
          "Next.js",
          "Vercel",
          "Пайдаланушы Аутентификациясы",
        ],
        image: "/images/branddos-1.png",
        domain: "https://branddos-aminas-projects-72d2f3c3.vercel.app/",
      },
    ],
    testimonials: [
      {
        name: "Айша Кәрімова",
        business: "Өрлеу Фитнес Залы",
        quote:
          "Brazilia Works біздің фитнес залымызға аса қажет кәсіби онлайн келбет берді. Іске қосылғаннан бері тапсырыстар 40%-ға өсті!",
      },
      {
        name: "Дәулет Ченов",
        business: "Ченов Тазалық, Ұлыбритания",
        quote:
          "Процесс өте оңай әрі жылдам болды. Жаңа веб-сайтымыз керемет көрінеді және біздің брендті толықтай көрсетеді. Ұсынамын.",
      },
      {
        name: "Елена Иванова",
        business: "Әдемі Сұлулық Салоны",
        quote:
          "Бағасы туралы алаңдадым, бірақ олардың ашық баға саясаты мені таң қалдырды. Уәде еткен нәрселерін уақытында және бюджетке сай орындады.",
      },
    ],
    faqs: [
      {
        q: "Веб-сайт жасау қанша уақыт алады?",
        a: "Стандартты бизнес веб-сайт әдетте біздің алғашқы кеңес беруден бастап іске қосылғанға дейін 1-3 апта уақыт алады. Бұл жобаның күрделілігіне байланысты өзгеруі мүмкін.",
      },
      {
        q: "Жұмысты бастау үшін сізге не қажет?",
        a: "Тек сіздің идеяларыңыз! Бізге сіздің көзқарасыңыз, бар мәтін мен суреттер (бұл жағынан көмектесе аламыз) және бизнес мақсаттарыңыз қажет. Қалғанын біз шешеміз.",
      },
      {
        q: "Сайт іске қосылғаннан кейін оны өзім жаңарта аламын ба?",
        a: "Әрине. Біз қолдануға оңай платформаларда жасаймыз және сізге мазмұнды жаңарту үшін бізге қоңырау шалмай-ақ істеуге болатын оқытуды ұсынамыз.",
      },
    ],
  },
  ru: {
    hero_title: "У вашего бизнеса нет сайта? Вы теряете клиентов и прибыль.",
    hero_subtitle:
      "На сегодняшнем рынке ваш сайт — это ваш самый мощный маркетинговый инструмент. Мы создаем доступные, высокопроизводительные сайты, которые привлекают новых клиентов и мгновенно вызывают доверие.",
    hero_cta: "Получить консультацию",
    why_title: "Отличный сайт — это не расход, а инвестиция в рост.",
    why_card_1_title: "Вас находят 24/7",
    why_card_1_desc:
      "Появляйтесь в поиске Google и привлекайте местных клиентов, которые активно ищут ваши услуги.",
    why_card_2_title: "Мгновенное доверие",
    why_card_2_desc:
      "Профессиональный сайт показывает, что вы — серьезный бизнес, и вызывает больше доверия, чем любая страница в социальных сетях.",
    why_card_3_title: "Автоматизируйте рост",
    why_card_3_desc:
      "Ваш сайт работает на вас круглосуточно: собирает заявки, записывает на прием и увеличивает продажи.",
    projects_title: "Бизнесы, которым мы помогли вырасти",
    testimonials_title: "Что говорят наши клиенты",
    why_us_title: "Почему выбирают Brazilia Works?",
    why_us_card_1_title: "Идеальный баланс",
    why_us_card_1_desc:
      "Получите полностью индивидуальный, высококачественный сайт без огромных ценников больших агентств. Мы — идеальный выбор для малого бизнеса.",
    why_us_card_2_title: "Мы — ваши партнеры",
    why_us_card_2_desc:
      "Мы — небольшая, преданная своему делу команда. Мы уделяем время, чтобы понять ваши цели, и лично заинтересованы в вашем успехе.",
    why_us_card_3_title: "Прозрачное ценообразование",
    why_us_card_3_desc:
      "Никаких сюрпризов или скрытых платежей. Мы предоставляем четкую, фиксированную смету на весь проект, начиная всего от 80 000 теңге.",
    faq_title: "Часто задаваемые вопросы",
    contact_title: "Есть идея? Давайте обсудим.",
    // --- REVISED TEXT: More confident and concise ---
    contact_subtitle:
      "У вас есть проект или просто вопрос? Буду рад(а) помочь.",
    form_name_label: "Полное имя",
    form_email_label: "Адрес электронной почты",
    form_message_label: "Расскажите нам о вашем проекте",
    form_submit_btn: "Отправить сообщение",
    form_alt_title: "Предпочитаете живое общение?",
    form_alt_desc:
      "Запишитесь на бесплатный 15-минутный звонок, чтобы обсудить ваши идеи напрямую с нашей командой. Без давления и обязательств.",
    form_alt_cta: "Записаться на звонок",
    form_email_direct_label: "Или напишите нам напрямую:",
    footer_tagline:
      "Создаем цифровые витрины, которые приносят результат. © 2024 Brazilia Works.",
    projects: [
      {
        title: "Urazbakhova & Partners",
        type: "Сайт корпоративной юридической фирмы",
        description:
          "Элегантный и профессиональный сайт для юридической фирмы Urazbakhova & Partners, созданный для демонстрации авторитета и доверия. Сайт представляет команду юристов, детализирует их области практики в корпоративном праве и укрепляет доверие через убедительные отзывы клиентов и официальные рекомендательные письма.",
        features: [
          "Элегантный и авторитетный дизайн",
          "Детализированные области практики",
          "Отзывы клиентов",
          "Многоязычный функционал",
        ],
        image: "/images/uip-1.png",
        domain: "https://uiplaw.kz",
      },
      {
        title: "Molshylyq",
        type: "E-commerce и Социальное Влияние",
        description:
          "Первая в Казахстане платформа по сокращению пищевых отходов. Это многопользовательское приложение соединяет продавцов с излишками еды с потребителями, создавая сообщество с реальным вкладом.",
        features: [
          "Full-Stack Приложение",
          "PostgreSQL",
          "Prisma",
          "Локализация",
        ],
        image: "/images/molshylyq-1.png",
        domain: "https://molshylyq.vercel.app/",
      },
      {
        title: "Sayahat Inc",
        type: "Frontend-витрина",
        description:
          "Визуально отточенная целевая страница для вымышленного турагентства, созданная для демонстрации идеальной верстки, адаптивного дизайна и плавных анимаций.",
        features: ["React", "Tailwind CSS", "Framer Motion", "UI/UX Дизайн"],
        image: "/images/sayahat-1.png",
        domain: "https://sayahat-eta.vercel.app",
      },
      {
        title: "Dayne",
        type: "AI Инструмент для Продуктивности",
        description:
          "Минималистичный учебный инструмент, который использует ИИ для создания персонализированных карточек и тестов из любого текста или файла, разработанный для максимальной концентрации.",
        features: [
          "AI Промптинг",
          "Supabase",
          "Парсинг файлов",
          "Двуязычный UI",
        ],
        image: "/images/study-app-1.png",
        domain: "https://study-app-three-black.vercel.app",
      },
      {
        title: "Branddos",
        type: "AI SaaS Платформа",
        description:
          "Помощник для социальных сетей на базе ИИ, который оптимизирует контент-стратегию для частных лиц и компаний. Мы довели его от концепции до монетизируемого продукта.",
        features: [
          "Интеграция с ИИ",
          "Next.js",
          "Vercel",
          "Аутентификация пользователей",
        ],
        image: "/images/branddos-1.png",
        domain: "https://branddos-aminas-projects-72d2f3c3.vercel.app/",
      },
    ],
    testimonials: [
      {
        name: "Аиша Каримова",
        business: "Фитнес-зал Orleu",
        quote:
          "Brazilia Works создали для нашего зала профессиональное онлайн-присутствие, в котором мы отчаянно нуждались. Количество записей увеличилось на 40% с момента запуска!",
      },
      {
        name: "Давид Чен",
        business: "Химчистка Чена, Великобритания",
        quote:
          "Процесс был безупречным и невероятно быстрым. Наш новый сайт выглядит потрясающе и идеально представляет наш бренд. Настоятельно рекомендую.",
      },
      {
        name: "Елена Иванова",
        business: "Салон красоты Ademi",
        quote:
          "Я беспокоилась о стоимости, но их прозрачное ценообразование стало глотком свежего воздуха. Они сделали именно то, что обещали, вовремя и в рамках бюджета.",
      },
    ],
    faqs: [
      {
        q: "Сколько времени занимает создание сайта?",
        a: "Стандартный бизнес-сайт обычно занимает 1-3 недели с момента нашей первоначальной консультации до запуска. Сроки могут варьироваться в зависимости от сложности проекта.",
      },
      {
        q: "Что от меня требуется для начала?",
        a: "Только ваши идеи! Нам нужны ваше видение, любой текст или изображения, которые у вас уже есть (мы можем с этим помочь), и ваши бизнес-цели. Остальное мы берем на себя.",
      },
      {
        q: "Смогу ли я обновлять сайт самостоятельно после запуска?",
        a: "Безусловно. Мы создаем сайты на удобных для пользователя платформах и предоставляем вам обучение, чтобы вы могли легко обновлять контент.",
      },
    ],
  },
};

const LanguageSwitcher = ({ lang, setLang }) => (
  <div className="flex items-center gap-1 p-1 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm shadow-sm">
    <button
      onClick={() => setLang("en")}
      className={`px-3 py-1 text-xs rounded-full transition-all duration-300 ${
        lang === "en"
          ? "bg-white/90 shadow-md font-semibold text-black"
          : "text-white/70 hover:text-white"
      }`}
    >
      EN
    </button>
    <button
      onClick={() => setLang("kz")}
      className={`px-3 py-1 text-xs rounded-full transition-all duration-300 ${
        lang === "kz"
          ? "bg-white/90 shadow-md font-semibold text-black"
          : "text-white/70 hover:text-white"
      }`}
    >
      KZ
    </button>
    <button
      onClick={() => setLang("ru")}
      className={`px-3 py-1 text-xs rounded-full transition-all duration-300 ${
        lang === "ru"
          ? "bg-white/90 shadow-md font-semibold text-black"
          : "text-white/70 hover:text-white"
      }`}
    >
      RU
    </button>
  </div>
);



function App() {
  const [lang, setLang] = useState("en");
  const colors = { primary: "#3D348B", light: "#F3F2EF", dark: "#1A1A1A" };
  const t = translations[lang] || translations.en;
  const ornamentImageUrl = "/ornament-strip.png";

  return (
    // THE FONT FIX: Apply a dynamic language class to the root element
    <div
      className={`lang-${lang}`}
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: colors.dark,
        color: colors.light,
      }}
    >
      <Helmet>
        <html lang={lang} />
        <title>
          Brazilia Works | High-Performance Websites for Small Businesses
        </title>
        <meta
          name="description"
          content="Affordable, professional websites that convert. Brazilia Works builds digital storefronts for small businesses that attract clients and build trust."
        />
        <meta
          name="keywords"
          content="web design, Kazakhstan, small business websites, affordable websites, branding, frontend developer, SaaS"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://brazilia.works/" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Brazilia Works",
            "url": "https://brazilia.works",
            "description": "We build modern, high-performance websites for small businesses in Kazakhstan and beyond.",
            "inLanguage": "${lang}",
            "author": {
              "@type": "Person",
              "name": "Amina Askar"
            }
          }
        `}</script>
      </Helmet>
      <style>{`
        /* Default header font for English */
        .syne-font { 
          font-family: 'Syne', sans-serif; 
        }

        /* Override header font for KZ and RU */
        .lang-kz .syne-font,
        .lang-ru .syne-font {
          font-family: 'Tektur', sans-serif;
        }

        @keyframes scroll { 
          to { 
            transform: translate(calc(-50% - 1rem)); 
          } 
        }
        .scrolling-wrapper { 
          animation: scroll 40s linear infinite; 
        }
      `}</style>

      <FloatingCursor colors={colors} />
      {/* MOBILE FIX: Reduced padding on mobile (p-6 -> p-4 md:p-6) */}
      <header className="fixed top-0 left-0 w-full z-50 p-4 md:p-6 flex justify-between items-center">
        <div className="syne-font text-xl md:text-2xl font-bold">
          Brazilia Works
        </div>
        <LanguageSwitcher lang={lang} setLang={setLang} />
      </header>

      <section className="relative min-h-[100dvh] w-full flex flex-col justify-center items-center text-center p-4 md:p-6 overflow-hidden bg-[#111111]">
        {/* 
        --- PNG Ornament Strip ---
        This uses your PNG image. You can customize its appearance below.
      */}
        <div
          className="absolute top-0 left-4 md:left-8 h-full w-12 md:w-16 pointer-events-none
                   bg-repeat-y bg-center opacity-30"
          style={{
            backgroundImage: `url(${ornamentImageUrl})`,
            backgroundSize: "100% auto",
          }}
        ></div>

        {/* The rest of your hero section content */}
        <div className="relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="syne-font text-4xl md:text-6xl lg:text-7xl font-extrabold max-w-4xl mx-auto leading-tight"
          >
            {/* CORRECT: Your language logic is now restored. */}
            {t.hero_title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-lg md:max-w-2xl mx-auto mt-5 md:mt-6 text-base md:text-xl text-white/80"
          >
            {/* CORRECT: Your language logic is now restored. */}
            {t.hero_subtitle}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-10"
          >
            <a
              href="#contact"
              className="syne-font text-base md:text-lg font-bold text-black bg-white px-8 py-3 md:px-10 md:py-4 rounded-full shadow-lg transition-transform hover:scale-105 whitespace-nowrap"
            >
              {/* CORRECT: Your language logic is now restored. */}
              {t.hero_cta}
            </a>
          </motion.div>
        </div>
      </section>

      {/* --- Section 2: Why a Website --- */}
      <section
        className="py-24 md:py-32"
        style={{ backgroundColor: colors.light, color: colors.dark }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="syne-font text-4xl md:text-6xl font-bold mb-6">
              {t.why_title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-20">
            <InfoCard
              theme="light"
              icon={<Search size={28} />}
              title={t.why_card_1_title}
            >
              {t.why_card_1_desc}
            </InfoCard>
            <InfoCard
              theme="light"
              icon={<CheckCircle size={28} />}
              title={t.why_card_2_title}
            >
              {t.why_card_2_desc}
            </InfoCard>
            <InfoCard
              theme="light"
              icon={<BarChart size={28} />}
              title={t.why_card_3_title}
            >
              {t.why_card_3_desc}
            </InfoCard>
          </div>
        </div>
      </section>

      {/* --- Section 3: Projects --- */}
      <section id="work" className="bg-black py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 space-y-24 md:space-y-32">
          <div className="text-center">
            <h2 className="syne-font text-4xl md:text-6xl font-bold">
              {t.projects_title}
            </h2>
          </div>
          {t.projects.map((project, index) => (
            <ProjectRow
              key={project.title}
              project={project}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </section>

      

      {/* --- Section 5: Why Choose Us --- */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="syne-font text-4xl md:text-6xl font-bold mb-6">
              {t.why_us_title}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-20">
            <InfoCard
              theme="dark"
              icon={<Award size={28} />}
              title={t.why_us_card_1_title}
            >
              {t.why_us_card_1_desc}
            </InfoCard>
            <InfoCard
              theme="dark"
              icon={<Users size={28} />}
              title={t.why_us_card_2_title}
            >
              {t.why_us_card_2_desc}
            </InfoCard>
            <InfoCard
              theme="dark"
              icon={<DollarSign size={28} />}
              title={t.why_us_card_3_title}
            >
              {t.why_us_card_3_desc}
            </InfoCard>
          </div>
        </div>
      </section>

      {/* --- Section 6: FAQ --- */}
      <section className="py-24 md:py-32 bg-gray-900/40">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center">
            <h2 className="syne-font text-4xl md:text-6xl font-bold mb-16">
              {t.faq_title}
            </h2>
          </div>
          <div className="space-y-4">
            {t.faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* --- Section 7: Contact Form --- */}
      <section id="contact" className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6">
          {/* Heading container */}
          <div className="text-center mb-12">
            {/* Main Headline */}
            <h2 className="syne-font text-4xl md:text-6xl font-bold">
              {t.contact_title || "Have an Idea? Let's Talk."}
            </h2>

            {/* UPDATED: Personal and confident sub-headline */}
            <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto">
              {t.contact_subtitle ||
                "Have a project in mind or just a question? I'd be happy to help."}
            </p>
          </div>

          {/* The ContactForm component remains the same */}
          <ContactForm t={t} colors={colors} />
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="text-center py-8 bg-black">
        <div className="flex justify-center gap-6 mb-4">
          <a
            href="https://github.com/brazilia"
            aria-label="GitHub"
            className="text-white/50 hover:text-white transition"
          >
            <Github />
          </a>
          <a
            href="https://www.linkedin.com/in/amina-askar-86475a370"
            aria-label="LinkedIn"
            className="text-white/50 hover:text-white transition"
          >
            <Linkedin />
          </a>
          <a
            href="mailto:braziliawork@gmail.com"
            aria-label="Email"
            className="text-white/50 hover:text-white transition"
          >
            <Mail />
          </a>
        </div>
        <p className="text-sm text-white/50">{t.footer_tagline}</p>
      </footer>
    </div>
  );
}

export default App;
